/**
 * Centralized hook for managing rate shop data transformation
 * Replaces scattered data transformation logic across components
 */

import { useMemo } from 'react';
import type { OtaiLowestRatesQuery } from '@/features/overview-table/gql/_gen_/rate-shop.gql';
import { DateHelpers } from '@/helpers/date-helpers';
import type { DailyRatesData } from '../../../context/report-data-context';
import { generateRateShopTable } from '../../../utils/rate-filters';
import type {
  ChartDataPoint,
  CompetitorInfo,
  RateShopData,
  RateShopHeader,
  RateType,
} from '../types/rate-shop-types';
import { transformDataForChart } from '../utils/rate-data-transformers';

interface UseRateShopDataProps {
  rateShopData?: OtaiLowestRatesQuery;
  lowestRates?: DailyRatesData;
  flexRates?: DailyRatesData;
  rateType: RateType;
}

interface UseRateShopDataReturn {
  tableData: RateShopData;
  chartData: ChartDataPoint[];
  competitorInfo: CompetitorInfo[];
  isEmpty: boolean;
  isLoading: boolean;
}

export function useRateShopData({
  rateShopData,
  lowestRates,
  flexRates,
  rateType,
}: UseRateShopDataProps): UseRateShopDataReturn {
  const processedData = useMemo(() => {
    // Priority 1: Use context data if available
    if (lowestRates && flexRates) {
      const selectedData = rateType === 'lowest' ? lowestRates : flexRates;

      // Convert string headers to RateShopHeader objects if needed
      const headers: RateShopHeader[] = selectedData.headers.map((title, index) => ({
        columnSize: 1,
        dataKey: `col_${index}`,
        title: typeof title === 'string' ? title : title,
        tooltip: typeof title === 'string' ? title : title,
      }));

      // Convert to our standardized format
      const standardizedData: RateShopData = {
        headers,
        rows: selectedData.rows || [],
      };

      return standardizedData;
    }

    // Priority 2: Fallback to rateShopData with transformation
    if (rateShopData?.dailyRates && rateShopData?.compSet) {
      const fallbackData = generateRateShopTable({
        dailyRates: rateShopData.dailyRates,
        compSet: rateShopData.compSet,
        startDate: DateHelpers.today(),
        endDate: DateHelpers.addDays(DateHelpers.today(), 90),
      });

      // Convert string headers to RateShopHeader objects if needed
      const fallbackHeaders: RateShopHeader[] = fallbackData.headers.map((title, index) => ({
        columnSize: 1,
        dataKey: `col_${index}`,
        title: typeof title === 'string' ? title : title,
        tooltip: typeof title === 'string' ? title : title,
      }));

      // Convert to standardized format
      return {
        headers: fallbackHeaders,
        rows: fallbackData.rows || [],
      };
    }

    // Return empty data structure
    return {
      headers: [],
      rows: [],
    };
  }, [rateShopData, lowestRates, flexRates, rateType]);

  const { chartData, competitorInfo } = useMemo(() => {
    if (!processedData.headers.length || !processedData.rows.length) {
      return { chartData: [], competitorInfo: [] };
    }

    return transformDataForChart(processedData);
  }, [processedData]);

  const isEmpty = processedData.rows.length === 0;
  const isLoading = false; // Loading state handled by parent components

  return {
    tableData: processedData,
    chartData,
    competitorInfo,
    isEmpty,
    isLoading,
  };
}
